import type { ReactNode } from "react";
import { GlobeIcon } from "lucide-react";

import type { BlogLocale } from "@/features/blog/locales";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { LOCALE_LABELS } from "@/features/blog/locales";

type LanguageSwitcherProps = {
  currentLocale: BlogLocale;
  locales: readonly BlogLocale[];
  renderLink: (locale: BlogLocale, children: ReactNode) => ReactNode;
  align?: "start" | "end";
};

export function LanguageSwitcher({
  currentLocale,
  locales,
  renderLink,
  align = "end",
}: LanguageSwitcherProps) {
  if (locales.length <= 1) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Grid glass pill, the same size as the blog filter pills. */}
        <button
          type="button"
          className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-full px-4 text-[14px] text-(--g-ink-2) transition-colors hover:text-(--g-ink)"
          style={{
            background: "rgb(240 242 241 / 62%)",
            boxShadow:
              "inset 0 1px 0 rgb(255 255 255 / 85%), inset 0 0 0 1px rgb(22 25 28 / 9%)",
          }}
        >
          <GlobeIcon className="size-3.5" strokeWidth={1.5} />
          {LOCALE_LABELS[currentLocale]}
        </button>
      </DropdownMenuTrigger>
      {/* Portaled outside the Grid theme, so the glass is spelled out here. */}
      <DropdownMenuContent
        align={align}
        className="min-w-[140px] rounded-[14px] border-0 p-1.5 font-[family-name:var(--font-geist)] text-[14px] text-[#16191C] backdrop-blur-xl"
        style={{
          background: "rgb(240 242 241 / 96%)",
          boxShadow:
            "inset 0 1px 0 rgb(255 255 255 / 85%), inset 0 0 0 1px rgb(22 25 28 / 9%), 0 18px 40px -18px rgb(0 0 0 / 35%)",
        }}
      >
        {locales.map((locale) => (
          <DropdownMenuItem key={locale} asChild>
            {renderLink(
              locale,
              <span
                className={
                  locale === currentLocale ? "font-medium" : "text-[#50565B]"
                }
              >
                {LOCALE_LABELS[locale]}
              </span>,
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
